import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { consume } from '@lit/context';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/textarea/textarea.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';

import { ltnDialogServiceContext } from '../../../../ns-services/contexts.js';
import { LtnDialogService } from '../../../../ltn-dialog/ltn-dialog-service.js';
import { nsLayoutStyles } from '../../../../ns-styles/index.js';

import NSJobComponent from '../../job.js';

import JobSchema from '../../../../models/job-schema.js';

import { callLambda } from '../../../../helpers/lambda.js';
import { claimJob, isUnclaimed } from '../../../../helpers/job-claim.js';
import { lambdaRefusal } from '../../../../helpers/public-ledger-balance-api.js';
import { viewPath } from '../../../../helpers/route.js';

import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-detail-list.js';
import {
  AUTO_PUBLISHED_TEXT,
  NOTES_MAX,
  SHOWN_DOMAINS,
  domainView,
  notesProblem,
  publicReasonProblem,
  publishResult,
  scoresBehaviourAfterPublish,
  withBehaviourScore,
  rescoreResult,
  reviewState,
  shownAsSuppressed,
  suppressResult,
  type ActionResult,
  type DomainView,
  type LambdaAnswer,
  type TrustScoreRecord,
} from './trust-score-review-logic.js';
import { confirmSuppress } from './confirm-suppress.js';
import definition from '@job-schemas/support/trust-score-review.json';
import { jobSchema } from '../../job-schema.js';

const dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const formatDate = (value: string | null) => {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : dateFormat.format(date);
};

/**
 * The nodeStream support card the trust-score/review-queue cron raises when a company's trust score waits for review: the
 * claimer compares the working score with the published one and publishes it (trust-score/publish) or suppresses it
 * (trust-score/suppress), and can re-check Companies House first (companies-house/trust-score/rescore). The card completes its
 * job after a successful Publish or Suppress; the lambdas never do. Publish is refused until every external check is scored,
 * which the card says in plain words. The Behavioural domains (Application interaction, Supply chain) are shown read-only: they
 * are published each night without review, and a Publish snapshots them with the rest. The nightly run skips a suppressed
 * company, so publishing one again also scores its behaviour at once (trust-score/behaviour-score).
 */
@customElement(NSSupportTrustScoreReview.is)
export default class NSSupportTrustScoreReview extends NSJobComponent {
  static get is() {
    return 'ns-support-trust-score-review';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, {
      outcomes: { published: { type: 'hidden' }, suppressed: { type: 'hidden' }, closed: { type: 'hidden' } },
    });
  }

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        --ns-detail-list-gap: var(--wa-space-2xs);
      }

      .domain-heading {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--wa-space-s);
      }

      /* The shared pill's bottom margin would sit it above the middle of the domain's name */
      .domain-heading .pill {
        margin: 0;
      }

      .categories {
        margin: 0;
        padding-left: var(--wa-space-l);
      }

      .categories ul {
        padding-left: var(--wa-space-l);
      }
    `,
  ];

  @consume({ context: ltnDialogServiceContext })
  private __dialog?: LtnDialogService;

  @state()
  private __record: TrustScoreRecord | null = null;

  @state()
  private __loaded = false;

  @state()
  private __busy = false;

  @state()
  private __rescoring = false;

  @state()
  private __notes = '';

  @state()
  private __message: string | null = null;

  private __subscribed = false;

  async connectedCallback() {
    await super.connectedCallback();
    await this.updateComplete;
    await this.__load();
  }

  private get __companyId(): string | null {
    const id = this._job?.inputValues?.subjectCompanyId;
    return typeof id === 'string' && id ? id : null;
  }

  private get __companyName(): string {
    const name = this._job?.inputValues?.subjectCompanyName;
    return typeof name === 'string' && name ? name : 'This company';
  }

  private get __claimedByMe(): boolean {
    const me = this._directory?.me?.id;
    return !!me && !!this._job && this._job.assignedTo === me;
  }

  // Reads the company's record (support reads all of it) and follows it, so a score that lands while the card is open shows
  private async __load() {
    const companyId = this.__companyId;
    if (!this._db || !companyId) {
      this.__loaded = true;
      return;
    }
    try {
      const found = await this._db.query('companyTrustScore', { companyId: { $eq: companyId } }, { bust: true });
      const [record] = (found.results ?? []) as unknown as TrustScoreRecord[];
      this.__record = record ?? null;
      if (record?.id && !this.__subscribed) {
        this.__subscribed = true;
        this._subscriptions.subscribe(`companyTrustScore.${record.id}.*`, () => void this.__load());
      }
    } catch (err) {
      this.showError(err, "This company's trust score couldn't be read. Please try again.");
    } finally {
      this.__loaded = true;
    }
  }

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._directory?.me?.id };
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  private async __call(path: string, body: object): Promise<LambdaAnswer> {
    const response = await callLambda(path, 'POST', this._auth?.token, JSON.stringify(body));
    return response.res as unknown as LambdaAnswer;
  }

  private async __finish(result: ActionResult) {
    if (result.kind === 'done') {
      this.__message = result.message;
      try {
        await this.outcome(result.outcome);
      } catch (err) {
        this.showError(err, `${result.message} This card couldn't be completed: please try again.`);
      }
      return;
    }
    if (result.kind === 'requeued') {
      this.__message = result.message;
      await this.__load();
      return;
    }
    this.showError(result.message);
    if (result.reload) await this.__load();
  }

  private async __publish() {
    const record = this.__record;
    const companyId = this.__companyId;
    if (!record || !companyId || this.__busy || this.__rescoring) return;
    const problem = notesProblem(this.__notes, false);
    if (problem) {
      this.showError(problem);
      return;
    }
    this.__busy = true;
    this.clearError();
    this.__message = null;
    this.loading('Publishing the trust score');
    try {
      const notes = this.__notes.trim();
      const answer = await this.__call('trust-score/publish', {
        companyId,
        expectedCalculatedAt: record.lastCalculatedAt,
        ...(notes ? { notes } : {}),
      });
      let result = publishResult(answer);
      // A company published again after a Suppress has its behaviour scored now, as the nightly run skipped it
      if (scoresBehaviourAfterPublish(record, answer)) {
        const behaviour = await this.__call('trust-score/behaviour-score', { companyId }).catch(() => null);
        result = withBehaviourScore(result, behaviour);
      }
      this.loaded();
      await this.__finish(result);
    } catch (err) {
      this.loaded();
      this.showError(err, "The score couldn't be published. Please try again.");
    } finally {
      this.__busy = false;
    }
  }

  private async __suppress() {
    const companyId = this.__companyId;
    if (!companyId || this.__busy || this.__rescoring) return;
    const problem = notesProblem(this.__notes, true);
    if (problem) {
      this.showError(problem);
      return;
    }
    const publicReason = await confirmSuppress(this.__dialog);
    if (publicReason === null) return;
    const reasonProblem = publicReasonProblem(publicReason);
    if (reasonProblem) {
      this.showError(reasonProblem);
      return;
    }
    this.__busy = true;
    this.clearError();
    this.__message = null;
    this.loading('Suppressing the trust score');
    try {
      const answer = await this.__call('trust-score/suppress', {
        companyId,
        notes: this.__notes.trim(),
        publicReason: publicReason.trim(),
      });
      this.loaded();
      await this.__finish(suppressResult(answer));
    } catch (err) {
      this.loaded();
      this.showError(err, "The score couldn't be suppressed. Please try again.");
    } finally {
      this.__busy = false;
    }
  }

  // Re-checks Companies House; Publish waits until it answers (owner decision)
  private async __rescore() {
    const companyId = this.__companyId;
    if (!companyId || this.__busy || this.__rescoring) return;
    this.__rescoring = true;
    this.clearError();
    this.__message = null;
    try {
      const result = rescoreResult(await this.__call('companies-house/trust-score/rescore', { companyId }));
      if (result.ok) this.__message = result.message;
      else this.showError(result.message);
      await this.__load();
    } catch (err) {
      this.showError(err, "Companies House couldn't be re-checked. Please try again.");
    } finally {
      this.__rescoring = false;
    }
  }

  private async __close() {
    if (this.__busy) return;
    this.__busy = true;
    try {
      await this.outcome('closed');
    } catch (err) {
      this.showError(err, "This card couldn't be closed. Please try again.");
    } finally {
      this.__busy = false;
    }
  }

  private __renderScore(view: DomainView) {
    const working = view.working;
    const rows = [
      { label: 'New score', value: working ? `${working.score ?? 'Unknown'}` : 'Not scored yet' },
      { label: 'Published', value: view.published ? `${view.published.score ?? 'Unknown'}` : 'Not published' },
      ...(working?.capApplied !== null && working?.capApplied !== undefined
        ? [{ label: 'Capped at', value: `${working.capApplied}: ${working.capReason ?? 'no reason given'}` }]
        : []),
      ...(working?.killed ? [{ label: 'Kill switch', value: working.killReason ?? 'Yes' }] : []),
      { label: 'Scored', value: formatDate(working?.scoredAt ?? null) },
    ];
    return html`
      <div class="job-panel">
        <div class="domain-heading">
          <h3>${view.label}</h3>
          ${view.changed
            ? html`
                <span class="pill pill--yellow">Changed since published</span>
              `
            : nothing}
        </div>
        ${view.autoPublished
          ? html`
              <p>${AUTO_PUBLISHED_TEXT[view.domain]}</p>
            `
          : nothing}
        <ns-detail-list layout="grid" .rows=${rows}></ns-detail-list>
        ${view.categories.length
          ? html`
              <ul class="categories">
                ${view.categories.map(
                  (category) => html`
                    <li>
                      ${category.name}: ${category.score ?? 'Unknown'}
                      ${category.reasons.length
                        ? html`
                            <ul>
                              ${category.reasons.map(
                                (reason) => html`
                                  <li>${reason}</li>
                                `,
                              )}
                            </ul>
                          `
                        : nothing}
                    </li>
                  `,
                )}
              </ul>
            `
          : nothing}
        ${view.domain === 'COMPANIES_HOUSE' && this.__claimedByMe && reviewState(this.__record) === 'waiting'
          ? html`
              <div>
                <wa-button
                  variant="brand"
                  appearance="outlined"
                  ?disabled=${this.__busy || this.__rescoring}
                  ?loading=${this.__rescoring}
                  @click=${() => this.__rescore()}
                >
                  Re-check Companies House
                </wa-button>
              </div>
            `
          : nothing}
      </div>
    `;
  }

  // What everyone sees beside the 0 while the score is suppressed, also while its review is reopened and not yet decided
  private __renderPublicReason(record: TrustScoreRecord) {
    if (!shownAsSuppressed(record)) return nothing;
    const reason = record.published?.suppressedReason;
    const reopened = record.manualReview?.publishDecision === 'REOPENED';
    const rows = [
      ...(reopened
        ? [{ label: 'Status', value: 'Suppressed, review reopened. Others see it as suppressed until you decide.' }]
        : []),
      ...(reason ? [{ label: 'Public reason', value: reason }] : []),
    ];
    if (rows.length === 0) return nothing;
    return html`
      <ns-detail-list layout="grid" .rows=${rows}></ns-detail-list>
    `;
  }

  private __renderBody() {
    if (!this.__loaded) return nothing;
    const record = this.__record;
    const state = reviewState(record);
    if (!record || state === 'missing') {
      return html`
        <div class="job-panel">
          <p role="status">This company has no trust score to review yet.</p>
        </div>
      `;
    }
    if (state === 'reviewed' && this._job?.status !== 'COMPLETE') {
      return html`
        <div class="job-panel">
          <h3>Already reviewed</h3>
          <p>
            This company's trust score was ${shownAsSuppressed(record) ? 'suppressed' : 'published'} on
            ${formatDate(record.manualReview?.reviewedAt ?? null)}, outside this card. Close the card.
          </p>
          ${this.__renderPublicReason(record)}
        </div>
      `;
    }
    const published = record.published?.publishedAt ? record.published : null;
    return html`
      ${this.__message
        ? html`
            <div class="job-panel">
              <p role="status">${this.__message}</p>
            </div>
          `
        : nothing}
      <div class="job-panel">
        <h3>Overall</h3>
        <ns-detail-list
          layout="grid"
          .rows=${[
            { label: 'New score', value: `${record.overall ?? 'Unknown'}` },
            { label: 'Published', value: published ? `${published.overall ?? 'Unknown'}` : 'Not published yet' },
            { label: 'Scored', value: formatDate(record.lastCalculatedAt ?? null) },
          ]}
        ></ns-detail-list>
        ${this.__renderPublicReason(record)}
        ${record.killSwitchActive
          ? html`
              <wa-callout variant="danger" appearance="filled-outlined">
                Kill switch: ${record.killSwitchReason ?? 'no reason given'}. The company's overall score is 0.
              </wa-callout>
            `
          : nothing}
      </div>
      ${SHOWN_DOMAINS.map((domain) => this.__renderScore(domainView(record, domain)))}
      ${this.__claimedByMe
        ? html`
            <div class="job-panel">
              <wa-textarea
                label="Notes (required to suppress)"
                maxlength=${NOTES_MAX}
                .value=${this.__notes}
                ?disabled=${this.__busy}
                @input=${(e: Event) => {
                  this.__notes = (e.target as HTMLTextAreaElement).value;
                }}
              ></wa-textarea>
            </div>
          `
        : nothing}
    `;
  }

  render() {
    const companyId = this.__companyId;
    return html`
      <ns-job-layout>
        <p slot="intro">
          Trust score waiting for review:
          ${companyId
            ? html`
                <a
                  href="/company/${companyId}"
                  @click=${(e: Event) => {
                    e.preventDefault();
                    viewPath(`/company/${companyId}`);
                  }}
                >
                  ${this.__companyName}
                </a>
              `
            : this.__companyName}
        </p>
        ${this.__renderBody()}
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>About this review</h3>
          <ul class="ticks">
            <li>
              <strong>Publish</strong>
              shows the new score to every company, and its checks are re-run every 24 to 30 days.
            </li>
            <li>
              <strong>Suppress</strong>
              shows every company a score of 0 with your public reason, and stops its re-checks, until it is published
              again.
            </li>
            <li>Until it is reviewed, other companies keep seeing the score published before.</li>
          </ul>
          <p class="note">Claim the card before you act; only the person who claimed it can.</p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE' || !this.__loaded) return nothing;
    if (isUnclaimed(job)) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>
          Claim
        </wa-button>
      `;
    }
    if (!this.__claimedByMe) return nothing;
    const state = reviewState(this.__record);
    if (state !== 'waiting') {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__close()}>
          Close
        </wa-button>
      `;
    }
    const disabled = this.__busy || this.__rescoring;
    return html`
      <wa-button variant="brand" type="button" ?disabled=${disabled} @click=${() => this.__publish()}>
        Publish
      </wa-button>
      <wa-button
        variant="brand"
        appearance="outlined"
        type="button"
        ?disabled=${disabled}
        @click=${() => this.__suppress()}
      >
        Suppress
      </wa-button>
    `;
  }
}
